import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { api } from '../../lib/api'
import { useSyncJob } from '../../lib/useSyncJob'
import { ClientSelect } from '../../components/ClientSelect'
import { useClientSelection } from '../../stores/clientSelection'
import { ListFilters, EMPTY_FILTERS, type FilterState } from '../../components/ListFilters'
import { COUNTRY_CURRENCY } from '@emag/shared'
import { Pagination } from '../../components/Pagination'
import { fmtMoney, fmtDate, ORDER_STATUS } from '../../lib/format'

interface OrderRow {
  id: number
  emagOrderId: string
  country: string
  status: number
  date: string
  customerName: string | null
  orderTotal: string
  isStorno: boolean
}
interface ListResp {
  items: OrderRow[]
  total: number
  page: number
  pageSize: number
  totals: { country: string; status: number; sum: number }[]
}

const TARI = ['RO', 'BG', 'HU'] as const
const STEAG: Record<string, string> = { RO: '🇷🇴', BG: '🇧🇬', HU: '🇭🇺' }

/** status 0 = anulată, 5 = returnată. Niciuna nu e vânzare. */
const STARI_FARA_VANZARE = [0, 5]

/**
 * Totalul vânzărilor, pe monede. Se calculează pe tot ce trece de filtre, nu doar pe
 * pagina afișată, și lasă afară comenzile anulate — ele au valoare în bază, dar n-au
 * fost vândute.
 */
function TotalVanzari({ totals }: { totals: { country: string; status: number; sum: number }[] }) {
  const pe = (fara: boolean) => {
    const m = new Map<string, number>()
    for (const t of totals) {
      const anulata = STARI_FARA_VANZARE.includes(t.status)
      if (anulata !== fara) continue
      const val = COUNTRY_CURRENCY[t.country as keyof typeof COUNTRY_CURRENCY] ?? t.country
      m.set(val, (m.get(val) ?? 0) + t.sum)
    }
    return [...m.entries()].filter(([, v]) => Math.abs(v) >= 0.005)
  }
  const vanzari = pe(false)
  const anulate = pe(true)
  if (!vanzari.length && !anulate.length) return null

  const scrie = (l: [string, number][]) =>
    l.map(([val, v]) => `${v.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${val}`).join(' · ')

  return (
    <>
      <span title="Suma comenzilor care trec de filtre, fără cele anulate sau returnate">
        Vânzări: <b className="text-b4l-text">{scrie(vanzari) || '0,00'}</b>
      </span>
      {anulate.length > 0 && (
        <span className="text-b4l-text-dim" title="Comenzi anulate sau returnate — nu intră în vânzări">
          anulate: {scrie(anulate)}
        </span>
      )}
    </>
  )
}

export function ComenziPage() {
  const clientId = useClientSelection((s) => s.selectedClientId)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  const [country, setCountry] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['emag-orders', clientId, page, pageSize, filters, country],
    queryFn: async () =>
      (await api.get<ListResp>('/emag/orders', { params: {
          clientId,
          ...(country ? { country } : {}),
          page,
          pageSize,
          ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
          ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
        } })).data,
    enabled: clientId != null,
  })

  const sync = useSyncJob('orders', clientId, {
    successText: (r) => `Sincronizate ${r.synced ?? 0} comenzi`,
    invalidate: ['emag-orders'],
  })

  // Butonul aduce intervalul din filtre; fără interval, serverul ia ultimele 30 de zile.
  // Înainte trimitea doar clientul, iar „Ian–Mar" din filtre nu aducea nimic din eMAG.
  function startSync() {
    void sync.start({
      ...(filters.dateFrom ? { createdAfter: `${filters.dateFrom} 00:00:00` } : {}),
      ...(filters.dateTo ? { createdBefore: `${filters.dateTo} 23:59:59` } : {}),
      ...(country ? { country } : {}),
    })
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-b4l-text">Comenzi</h1>
        <div className="flex items-center gap-2">
          <ClientSelect />
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setPage(1) }}
            className="rounded-md border border-b4l-border px-2.5 py-1.5 text-[13px] outline-none focus:border-b4l-green"
          >
            <option value="">Toate țările</option>
            {TARI.map((t) => (
              <option key={t} value={t}>{STEAG[t]} {t}</option>
            ))}
          </select>
          <button
            onClick={startSync}
            disabled={clientId == null || sync.running}
            className="flex items-center gap-1.5 rounded-md bg-b4l-green px-3 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60"
          >
            <RefreshCw size={14} className={sync.running ? 'animate-spin' : ''} />
            {sync.running ? 'Sincronizare în curs…' : 'Sincronizează'}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <ListFilters value={filters} onChange={(v) => { setFilters(v); setPage(1) }} searchPlaceholder="nr. comandă, client, factură…" />
      </div>

      {/* fara overflow-hidden: ar anula pozitionarea lipicioasa a antetului */}
      <div className="rounded-xl border border-b4l-border bg-white">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-b4l-border bg-white text-left text-b4l-text-muted shadow-[0_1px_0_0_rgb(229,231,235)]">
              <th className="px-4 py-2.5 font-medium">Țara</th>
              <th className="px-4 py-2.5 font-medium">Comandă</th>
              <th className="px-4 py-2.5 font-medium">Data</th>
              <th className="px-4 py-2.5 font-medium">Client</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {(isLoading || clientId == null) && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-b4l-text-dim">
                  {clientId == null ? 'Alege un client.' : 'Se încarcă…'}
                </td>
              </tr>
            )}
            {!isLoading && clientId != null && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-b4l-text-dim">
                  Nicio comandă. Apasă „Sincronizează".
                </td>
              </tr>
            )}
            {items.map((o) => (
              <tr key={o.id} className="border-b border-b4l-border last:border-0">
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {STEAG[o.country] ?? ''} {o.country}
                </td>
                <td className="px-4 py-2.5 font-medium">{o.emagOrderId}</td>
                <td className="px-4 py-2.5">{fmtDate(o.date)}</td>
                <td className="px-4 py-2.5">{o.customerName ?? '—'}</td>
                <td className="px-4 py-2.5">{ORDER_STATUS[o.status] ?? o.status}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoney(o.orderTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPage={setPage}
          onPageSize={setPageSize}
          unit="comenzi"
          summary={<TotalVanzari totals={data?.totals ?? []} />}
        />
      </div>

    </div>
  )
}
